import React from 'react';
import { useTranslation } from 'react-i18next';
import ColumnChartComponent from '../../components/Chat/ColumnChartComponent';
import { FiCreditCard, FiDollarSign, FiEye, FiEyeOff, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { StatCard } from '../../components/ui/StatCard';
import { ErrorState } from '../../components/ui/States';
import { Card } from '../../components/ui/Card';
import { useDashboardSummary } from '../../hooks/useDashboardSummary';
import { useSession } from '../../context/SessionContext';
import { isCustomer } from '../../utils/permissions';

const FinancialCardsSkeleton = ({ label }) => (
  <div className='dashboard-reveal' aria-label={label}>
    <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
      {Array.from({ length: 4 }).map((_, index) => (
        <Card key={index} className='p-5'>
          <div className='flex items-start justify-between gap-4'>
            <div className='w-full space-y-3'>
              <div className='h-3 w-24 animate-pulse rounded bg-slate-200/80 dark:bg-slate-700/70' />
              <div className='h-7 w-3/4 animate-pulse rounded bg-slate-200/80 dark:bg-slate-700/70' />
              <div className='h-3 w-full animate-pulse rounded bg-slate-200/80 dark:bg-slate-700/70' />
            </div>
            <div className='h-11 w-11 animate-pulse rounded-[var(--radius-md)] bg-slate-200/80 dark:bg-slate-700/70' />
          </div>
        </Card>
      ))}
    </div>

  </div>
);

export const Dashboard = () => {
  const { t } = useTranslation();
  const { role } = useSession();
  const [showValue, setShowValue] = React.useState(false);
  const { summary, loading, error, retry } = useDashboardSummary();
  const totals = summary?.totals || { purchases: 0, payments: 0, returns: 0, balance: 0 };
  const totalsIncludingPending = summary?.totalsIncludingPending || null;
  const customers = summary?.customers || [];
  const showPendingBreakdown = isCustomer(role) && Boolean(totalsIncludingPending);
  const customerCardTitle = (defaultTitle, approvedTitle) => t(
    showPendingBreakdown ? approvedTitle : defaultTitle,
  );
  const pendingCardProps = (secondaryValue, secondaryLabel) => showPendingBreakdown ? {
    secondaryLabel: t(secondaryLabel),
    secondaryValue,
  } : {};

  const handleShowValue = () => {
    setShowValue(!showValue);
  };

  return (
    <div>
      <PageHeader
        title={t('Dashboard')}
        description={t('Review the current financial position and customer activity.')}
        actions={
          <Button variant='secondary' onClick={handleShowValue} aria-pressed={showValue}>
            {showValue ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}
            {showValue ? t('Hide financial values') : t('Show financial values')}
          </Button>
        }
      />

      {error && (
        <div className='mb-6'>
          <ErrorState title={t('Unable to load dashboard data')} description={t('Try refreshing the page.')} />
          <div className='mt-3'>
            <Button variant='secondary' onClick={retry} disabled={loading}>
              {t('Try again')}
            </Button>
          </div>
        </div>
      )}

      {showValue ? (
        <div className='dashboard-reveal'>
          <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
            <StatCard
              title={customerCardTitle('Total Purchases', 'Received and approved purchases')}
              value={totals.purchases}
              icon={FiTrendingUp}
              hint={t('Real data from approved and pending transaction states')}
              loading={loading}
              {...pendingCardProps(totalsIncludingPending?.purchases, 'Total purchases including pending approval or receipt')}
            />
            <StatCard
              title={customerCardTitle('Total Payments', 'Approved payments')}
              value={totals.payments}
              icon={FiCreditCard}
              hint={t('Payments and returns reduce the balance')}
              loading={loading}
              tone='danger'
              {...pendingCardProps(totalsIncludingPending?.payments, 'Total payments including pending approval')}
            />
            <StatCard
              title={customerCardTitle('Total Returns', 'Approved returns')}
              value={totals.returns}
              icon={FiTrendingDown}
              hint={t('Returns reported by the transaction service')}
              loading={loading}
              tone='warning'
              {...pendingCardProps(totalsIncludingPending?.returns, 'Total returns including pending approval')}
            />
            <StatCard
              title={customerCardTitle('Total Balance', 'Confirmed transactions balance')}
              value={totals.balance}
              icon={FiDollarSign}
              hint={t('Purchases minus payments and returns')}
              loading={loading}
              tone='success'
              {...pendingCardProps(totalsIncludingPending?.balance, 'Projected billing balance including pending transactions')}
            />
          </div>

        </div>
      ) : (
        <FinancialCardsSkeleton label={t('Financial values are hidden')} />
      )}

      <div className='mt-6 grid min-w-0 gap-6 lg:grid-cols-2'>
        <ColumnChartComponent
          type='invoice'
          customers={customers}
          loading={loading}
          error={error ? t('Unable to load chart data') : ''}
          showFinancialValues={showValue}
        />
        <ColumnChartComponent
          type='payment'
          customers={customers}
          loading={loading}
          error={error ? t('Unable to load chart data') : ''}
          showFinancialValues={showValue}
        />
      </div>
    </div>
  );
};
